import { createSelector } from 'reselect';

import { CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID } from '../../../constants/bucket';
import {
  isMarketplaceBucketItem,
  TBucketItem,
  TCreateSendBucketItem,
  TMarketplaceBucketItem,
} from '../../../types/bucket';
import { TPygOption } from '../../../types/createSendContents';
import { EngagementReminderIntervalsEnum } from '../../../types/reports';
import {
  isIeCatalogOnlySend,
  isMarketplaceOnlySend,
  resolveCreateSendSource,
  TCreateSendSource,
  toContentsSource,
} from '../../../utils/createSendSource';
import { isItemDigital, isItemMsku } from '../../../utils/inventories';
import { resolveMarketplaceSupplierLock } from '../../../utils/marketplaceSupplier';
import { orderPygOptions } from '../../../utils/pygOptions';
import { IAppState } from '../../reducers';

export const selectCsContentsState = (state: IAppState) => state.createSend.contents;

export const selectCsContentsActiveTab = createSelector(selectCsContentsState, (contents) => contents.activeTab);

export const selectCsKits = createSelector(selectCsContentsState, (contents) => contents.kits);

export const selectCsItems = createSelector(selectCsContentsState, (contents) => contents.items);

export const selectCsBoxId = createSelector(selectCsContentsState, (contents) => contents.boxId);

export const selectCsKitIds = createSelector(selectCsKits, (kits) => kits?.map(({ box_id }) => box_id));

// A marketplace pick is a physical product: never digital, never inventory-MSKU.
const isDigitalContent = (item: TCreateSendBucketItem): boolean =>
  !isMarketplaceBucketItem(item) && isItemDigital(item);

const isMskuContent = (item: TCreateSendBucketItem): boolean => !isMarketplaceBucketItem(item) && isItemMsku(item);

export const selectCsIsContentDigital = createSelector(selectCsKits, selectCsItems, (kits, items): boolean => {
  const allItems: TCreateSendBucketItem[] = [...(kits ?? []).flatMap((kit) => kit.items ?? []), ...(items ?? [])];

  return allItems.length > 0 && allItems.every(isDigitalContent);
});

export const selectCsHasMskuContent = createSelector(selectCsKits, selectCsItems, (kits, items): boolean =>
  ([...(kits ?? []).flatMap((kit) => kit.items ?? []), ...(items ?? [])] as TCreateSendBucketItem[]).some(
    isMskuContent,
  ),
);

export const selectCsSelectedKit = createSelector(selectCsKits, (kits) => (kits?.length === 1 ? kits[0] : null));

export const selectCsIsBusinessReasonRequiredByCampaign = createSelector(selectCsKits, (kits): boolean =>
  (kits ?? []).some((kit) => Boolean(kit.business_reason_required)),
);

export const selectCsKitReminderDefault = createSelector(selectCsSelectedKit, (kit): string => {
  const interval = kit?.receiver_reminder_interval;

  return !interval || interval === EngagementReminderIntervalsEnum.Custom ? '' : interval;
});

export const selectCsOnlyDigitalItemNames = createSelector(selectCsItems, (items) =>
  items.filter(isDigitalContent).map((item) => item.name),
);

export const selectCsGiftBudget = createSelector(selectCsContentsState, (contents) => contents.giftBudget ?? null);

export const selectCsHasGiftBudget = createSelector(selectCsGiftBudget, (budget): boolean => budget !== null);

export const selectCsIsGiftBudgetPending = createSelector(selectCsContentsState, (contents): boolean =>
  Boolean(contents.isGiftBudgetPending),
);

// Kits never hold marketplace items (they are IE campaigns), so `items` is the whole story.
export const selectCsHasMarketplaceContent = createSelector(selectCsItems, (items): boolean =>
  (items ?? []).some(isMarketplaceBucketItem),
);

export const selectCsMarketplaceItems = createSelector(selectCsItems, (items): TMarketplaceBucketItem[] =>
  (items ?? []).filter(isMarketplaceBucketItem),
);

export const selectCsIeItems = createSelector(selectCsItems, (items): TBucketItem[] =>
  (items ?? []).filter((item): item is TBucketItem => !isMarketplaceBucketItem(item)),
);

export const selectCsIsMarketplaceCustomCart = createSelector(
  selectCsBoxId,
  (boxId): boolean => boxId === CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID,
);

export const selectCsMarketplacePygPicks = createSelector(
  selectCsMarketplaceItems,
  selectCsIsMarketplaceCustomCart,
  (items, isCustomCart): TMarketplaceBucketItem[] => (isCustomCart ? [] : items),
);

export const selectCsPygOptionOrder = createSelector(selectCsContentsState, (contents) => contents.pygOptionOrder);

export const selectCsOrderedPygOptions = createSelector(
  selectCsKits,
  selectCsMarketplacePygPicks,
  selectCsPygOptionOrder,
  (kits, picks, order): TPygOption[] => orderPygOptions(kits ?? [], picks, order),
);

export const selectCsMarketplaceCartItems = createSelector(
  selectCsMarketplaceItems,
  selectCsIsMarketplaceCustomCart,
  (items, isCustomCart): TMarketplaceBucketItem[] => (isCustomCart ? items : []),
);

export const selectCsMarketplaceSupplierLock = createSelector(
  selectCsMarketplaceCartItems,
  resolveMarketplaceSupplierLock,
);

/**
 * What the send's contents are sourced from. The single place the rest of the flow should ask
 * — a campaign id or link slug cannot answer it, since either can reach the same content.
 */
export const selectCsContentsSource = createSelector(
  selectCsKits,
  selectCsItems,
  (kits, items): TCreateSendSource => resolveCreateSendSource({ kits, items }),
);

/**
 * True when this is a marketplace send rather than a portal-campaign send. A mixed cart is
 * deliberately false here — use `selectCsHasMarketplaceContent` for "touches the marketplace".
 */
export const selectCsIsMarketplaceOnlySend = createSelector(selectCsKits, selectCsItems, (kits, items): boolean =>
  isMarketplaceOnlySend({ kits, items }),
);

/** True when nothing in the send comes from the marketplace (empty contents included). */
export const selectCsIsIeCatalogOnlySend = createSelector(selectCsKits, selectCsItems, (kits, items): boolean =>
  isIeCatalogOnlySend({ kits, items }),
);

/** The resolved source in Contents-step vocabulary; `null` when mixed or empty. */
export const selectCsContentsSourceToggle = createSelector(selectCsContentsSource, toContentsSource);
